import { useState } from "react";
import { Check } from "lucide-react";
import { api } from "@/lib/api";

const inputCls =
  "w-full bg-[#060b14] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2a5fdb] transition-colors";

const LEVELS = ["Reps", "Local Comp/School", "Just starting out"];

export const BookingRequestForm = () => {
  const [form, setForm] = useState({
    player_name: "", player_dob: "", parent_name: "", phone: "", email: "", level: LEVELS[0],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const canSubmit =
    form.player_name.trim() && form.player_dob && form.parent_name.trim() &&
    form.phone.trim() && form.email.trim() && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/booking-requests", form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="booking-request-success"
        className="border border-white/10 bg-[#0a1428] p-8 md:p-12 max-w-2xl"
      >
        <div className="w-12 h-12 border border-[#2a5fdb] flex items-center justify-center mb-6">
          <Check className="text-[#2a5fdb]" />
        </div>
        <h3 className="font-display uppercase text-3xl tracking-tight leading-none">
          Request <span className="text-[#2a5fdb]">sent.</span>
        </h3>
        <p className="mt-4 text-white/60 text-sm leading-relaxed max-w-md">
          Thanks — Owen has your details and will text you shortly to lock in a
          time that works. Need him sooner? Call or text{" "}
          <a href="sms:+61458792779" className="text-white underline underline-offset-4">0458 792 779</a>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="booking-request-form"
      className="border border-white/10 bg-[#0a1428] p-6 md:p-10 max-w-2xl"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Player's name *</span>
          <input data-testid="request-input-player" className={`${inputCls} mt-2`} placeholder="Player name" value={form.player_name} onChange={setField("player_name")} />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Player's date of birth *</span>
          <input data-testid="request-input-dob" type="date" className={`${inputCls} mt-2 [color-scheme:dark]`} value={form.player_dob} onChange={setField("player_dob")} />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Parent's name *</span>
          <input data-testid="request-input-parent" className={`${inputCls} mt-2`} placeholder="Parent name" value={form.parent_name} onChange={setField("parent_name")} />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Phone *</span>
          <input data-testid="request-input-phone" type="tel" className={`${inputCls} mt-2`} placeholder="04xx xxx xxx" value={form.phone} onChange={setField("phone")} />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Email *</span>
          <input data-testid="request-input-email" type="email" className={`${inputCls} mt-2`} placeholder="you@email.com" value={form.email} onChange={setField("email")} />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Current level of play *</span>
          <select data-testid="request-input-level" className={`${inputCls} mt-2`} value={form.level} onChange={setField("level")}>
            {LEVELS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </label>
      </div>
      {error && <p data-testid="booking-request-error" className="mt-4 text-red-400 text-sm">{error}</p>}
      <button
        data-testid="booking-request-submit"
        type="submit"
        disabled={!canSubmit}
        className="btn-sharp mt-6 w-full sm:w-auto justify-center disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? "Sending…" : "Send booking request"}
        <span aria-hidden>→</span>
      </button>
      <p className="mt-4 text-white/40 text-xs leading-relaxed">
        Owen will text you back to arrange session times — no payment needed now.
      </p>
    </form>
  );
};
